<template>
    <div class="col-span-4 lg:col-span-4">
        <analytics-sidebar-box
            data-test="locked-snippet-box"
            :canReset="selectedSnippet !== undefined"
            @reset="selectedSnippet = undefined"
        >
            <template v-slot:title> Locked Snippet </template>
            <vx-selectable
                size="sm"
                data-test="locked-snippet-filter"
                placeholder="All"
                label="title"
                :loading="loading"
                :clearable="false"
                @search="search"
                :options="snippets"
                :reduce="(option: Snippet) => ({ id: option.id, title: option.title })"
                v-model="selectedSnippet"
            >
            </vx-selectable>
        </analytics-sidebar-box>
    </div>
</template>

<script lang="ts" setup>
import { VxSelectable } from '@voxie/frontend-components';
import { computed, onMounted, ref, watch } from 'vue';
import { useSearchRequest } from '~/composables/useSearchRequest';
import { snippetsClient } from '~/services';
import type { Snippet } from '~/types/snippet.types';
import AnalyticsSidebarBox from '../partial/sidebar/AnalyticsSidebarBox.vue';
import type { ClientContext } from '~/services/contextClient';

const teamId = Spark.state.currentTeam.id;

const props = defineProps({
    orgUnitId: {
        type: Number,
    },
});

const model = defineModel({ type: Number });

const {
    loading,
    response: snippets,
    createSearch,
    cancelKey,
} = useSearchRequest<Snippet[]>(snippetsClient, 'snippets-search');

const search = createSearch(async (query: string) => {
    const context: ClientContext = {
        teamId,
        ...(props.orgUnitId && { orgUnitId: props.orgUnitId }),
    };

    const params = {
        query,
        filter: { is_locked: 1 },
    };

    const { data: response } = await snippetsClient.getSnippets(context, params, { cancelKey });

    return response.data;
});

onMounted(() => {
    search();
});

const selectedSnippet = ref<{ id?: number; title: string }>();

watch(
    () => selectedSnippet.value?.id,
    () => {
        model.value = selectedSnippet.value ? selectedSnippet.value.id : undefined;
    }
);

const shouldFetchSingleSnippet = computed(() => {
    return model.value && !snippets.value?.find((snippet) => snippet.id === model.value);
});

const fetchSingleSnippet = async () => {
    if (!shouldFetchSingleSnippet.value || !model.value) {
        return;
    }

    try {
        const context = {
            teamId,
            ...(props.orgUnitId && { orgUnitId: props.orgUnitId }),
        };

        const { data: snippet } = await snippetsClient.getSnippet(context, model.value);
        selectedSnippet.value = {
            id: snippet.id,
            title: snippet.title,
        };
    } catch (error) {
        console.error(error);
        selectedSnippet.value = {
            id: model.value,
            title: String(model.value),
        };
    }
};

watch(
    () => model.value,
    () => {
        fetchSingleSnippet();
    },
    {
        immediate: true,
    }
);
</script>
